import { Application, EventEmitter, Renderer, RenderLayer } from "pixi.js";
import {
  CommonEventMap,
  CommonEvents,
  GameEventMap,
  MessageEventMap,
} from "../events/EventEnums";
import { JackpotHitData } from "../models/JackpotHitData";
import { CurrentJackpotValue } from "../models/JackpotCurValue";

type GeneralSettings = {
  showTimes: boolean;
  showMachineIdOnHitScreen: boolean;
  showDateOnHitScreen: boolean;
  showTimeOnHitScreen: boolean;
  showMachineIdInHistory: boolean;
  showTimeInHistory: boolean;
  currencySymbol: string;
  showDecimals: boolean;
};

type AppTimings = {
  jackpotHitScreenDurationSeconds: number;
  currentJackpotScreenDurationSeconds: number;
  historyScreenDurationSeconds: number;
};

const GlobalStore = (() => {
  const layers = {
    background: new RenderLayer(),
    content: new RenderLayer(),
    loader: new RenderLayer(),
    status: new RenderLayer(),
    debug: new RenderLayer({
      sortableChildren: true,
    }),
  };

  let _app: Application<Renderer> | null = null;
  let _language: string = "";
  let _theme: string = "";
  let _ip: string = "";
  let _port: number = 0;
  let _activeJackpotIds: Set<number> = new Set<number>();
  let _jackpotHit: JackpotHitData | null = null;
  let _signId: string = "";
  let _jackpotIdOverride: number | null = null;
  let _JackpotHistory: JackpotHitData[] = [];
  let _JackpotCurrentValues: Map<number, CurrentJackpotValue> = new Map();

  let generalSettings: GeneralSettings = {
    showTimes: false,
    showMachineIdOnHitScreen: true,
    showDateOnHitScreen: true,
    showTimeOnHitScreen: true,
    showMachineIdInHistory: true,
    showTimeInHistory: true,
    currencySymbol: "RD$",
    showDecimals: true,
  };

  let timings: AppTimings = {
    jackpotHitScreenDurationSeconds: 45,
    currentJackpotScreenDurationSeconds: 30,
    historyScreenDurationSeconds: 10,
  };

  const commonEventEmitter: EventEmitter<CommonEventMap> = new EventEmitter();
  const messageEventEmitter: EventEmitter<MessageEventMap> = new EventEmitter();
  const gameEventEmitter: EventEmitter<GameEventMap> = new EventEmitter();

  let _currencyFormatter:
    Intl.NumberFormat | ((value: number) => string) | null = null;

  let _locale: {
    date: string;
    currency: string;
  } = {
    date: "en-US",
    currency: "en-US",
  };

  const currencyFormatter: (value: number) => string = (
    value: number,
  ): string => {
    if (_currencyFormatter === null) {
      console.warn("No currency formatter defined");
      return value.toString();
    }
    // Handle both Intl.NumberFormat and custom function cases
    if (typeof _currencyFormatter === "function") {
      return (_currencyFormatter as (value: number) => string)(value);
    } else {
      return _currencyFormatter.format(value);
    }
  };

  return {
    // layers,
    commonEventEmitter,
    messageEventEmitter,
    gameEventEmitter,

    generalSettings,
    timings,

    setLocale: ({ date, currency }: { date: string; currency: string }) => {
      if (date !== undefined) _locale.date = date;
      if (currency !== undefined) _locale.currency = currency;
    },
    getDateLocale: (): string => _locale.date,
    getCurrencyLocale: (): string => _locale.currency,

    setCurrencyFormatter: (formatter: (value: number) => string) => {
      _currencyFormatter = formatter;
    },
    formatCurrency: (value: number) => currencyFormatter(value),

    setCurJackpot: (key: number, value: CurrentJackpotValue) => {
      _JackpotCurrentValues.set(key, value);
    },
    getCurJackpots: (): CurrentJackpotValue[] => {
      return Array.from(_JackpotCurrentValues.values());
    },
    getCurJackpot: (key: number): CurrentJackpotValue | undefined => {
      return _JackpotCurrentValues.get(key);
    },

    getLayers: (): Readonly<typeof layers> => layers,
    addActiveJackpotId: (id: number) => {
      _activeJackpotIds.add(id);
      commonEventEmitter.emit(CommonEvents.UPDATED_CONFIG);
    },
    getActiveJackpots: (): ReadonlySet<number> => _activeJackpotIds,
    clearActiveJackpots: (): void => {
      _activeJackpotIds.clear();
      commonEventEmitter.emit(CommonEvents.UPDATED_CONFIG);
    },
    isJackpotActive: (id: number): boolean => _activeJackpotIds.has(id),

    getApp: () => _app,
    setApp: (app: Application<Renderer>) => {
      _app = app;
    },

    getLanguage: () => _language,
    setLanguage: (lang: string) => {
      _language = lang;
      commonEventEmitter.emit(CommonEvents.CHANGE_LANGUAGE, lang);
    },

    getTheme: () => _theme,
    setTheme: (theme: string) => {
      _theme = theme;
      commonEventEmitter.emit(CommonEvents.CHANGE_THEME, theme);
    },

    getIP: () => _ip,
    setIP: (ip: string) => {
      _ip = ip;
    },

    getPort: () => _port,
    setPort: (port: number) => {
      _port = port;
    },

    setJackpotHitData: (data: JackpotHitData | null) => {
      _jackpotHit = data;
    },
    getJackpotHitData: (): JackpotHitData | null =>
      _jackpotHit ? { ..._jackpotHit } : null,

    setSignId: (signId: string): void => {
      _signId = signId;
    },
    getSignId: (): string => `${_signId}`,

    setJackpotIdOverride: (jackpotId: number | null): void => {
      _jackpotIdOverride = jackpotId;
    },
    getJackpotIdOverride: (): number | null => {
      return _jackpotIdOverride;
    },

    setJackpotHistory: (history: JackpotHitData[]) => {
      _JackpotHistory = [...history];
    },
    getJackpotHistory: (): JackpotHitData[] => [..._JackpotHistory],
  };
})();

export default GlobalStore;
